import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import LabelAppIcon from './label-app-icon';
import {
  COPY_MODES,
  COPY_MODE_DESCRIPTIONS,
  COPY_MODE_LABELS,
  CreateLabelForm,
  LABEL_LIMITS,
} from './label-schema';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface StepLabelInformationProps {
  control: Control<CreateLabelForm>;
  errors: FieldErrors<CreateLabelForm>;
  /**
   * Google doesn't allow changing the learn more link or the apps after create, so they lock once
   * the label exists. Copy mode stays editable (updateLabelCopyMode works on existing labels).
   */
  isLabelCreated: boolean;
  /** Edit Label: explains that copy mode changes apply right away, without publishing. */
  copyModeNote?: string;
}

export default function StepLabelInformation({
  control,
  errors,
  isLabelCreated,
  copyModeNote,
}: StepLabelInformationProps) {
  const driveEnabled = useWatch({ control, name: 'driveEnabled' });

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Label Information
        </Typography>
      </Box>
      <Controller
        name="title"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            id="label-title"
            label="Label name"
            required
            fullWidth
            slotProps={{
              htmlInput: { maxLength: LABEL_LIMITS.maxLabelNameLength },
            }}
            error={!!errors.title}
            helperText={errors.title?.message}
          />
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            id="label-description"
            label="Label description (optional)"
            fullWidth
            slotProps={{
              htmlInput: { maxLength: LABEL_LIMITS.maxLabelDescriptionLength },
            }}
            error={!!errors.description}
            helperText={errors.description?.message}
          />
        )}
      />
      <Controller
        name="learnMoreUri"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            id="label-learn-more"
            label="Learn more link (optional)"
            fullWidth
            disabled={isLabelCreated}
            error={!!errors.learnMoreUri}
            helperText={
              errors.learnMoreUri?.message ??
              (isLabelCreated
                ? "The learn more link can't be changed after the draft is created."
                : 'Add it now; it can’t be changed after you save the draft or publish.')
            }
          />
        )}
      />

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Applications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose where people can use this label. Only users with permission can
          apply it.
        </Typography>
      </Box>

      <Stack spacing={1} sx={{ pl: 1 }}>
        <Controller
          name="driveEnabled"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  id="label-app-drive"
                  disabled={isLabelCreated}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <LabelAppIcon app="DRIVE" size={20} />
                  <Typography>Drive and Docs</Typography>
                </Stack>
              }
            />
          )}
        />
        <Box sx={{ pl: 4, maxWidth: 460 }}>
          <Controller
            name="copyMode"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" disabled={!driveEnabled}>
                <InputLabel id="label-copy-mode-label">
                  When copying files
                </InputLabel>
                <Select
                  {...field}
                  id="label-copy-mode"
                  labelId="label-copy-mode-label"
                  label="When copying files"
                  renderValue={(mode) => COPY_MODE_LABELS[mode]}
                >
                  {COPY_MODES.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      <Stack>
                        <Typography variant="body2">
                          {COPY_MODE_LABELS[mode]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {COPY_MODE_DESCRIPTIONS[mode]}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                {copyModeNote && driveEnabled && (
                  <FormHelperText>{copyModeNote}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>
        <Controller
          name="gmailEnabled"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  id="label-app-gmail"
                  disabled={isLabelCreated}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <LabelAppIcon app="GMAIL" size={20} />
                  <Typography>Gmail</Typography>
                </Stack>
              }
            />
          )}
        />
        <FormHelperText>
          {isLabelCreated
            ? "Applications can't be changed after the draft is created."
            : 'Choose apps now; they can’t be changed after you save the draft or publish.'}
        </FormHelperText>
      </Stack>
    </Stack>
  );
}
